// =============================================
// Node Content Context — warstwa pośrednia mapa → Content Engine
// Buduje pełny kontekst wejściowy dla node'a z danych systemu
// =============================================

import { supabase } from "@/integrations/supabase/client";
import type { NodeContentContext } from "@/types/thematicMaps";
import { logPublic, logDebug, logError } from "./contentLogger";
import { getWpPostsForLinking } from "@/services/existingPostMatcher";

const MAX_ENTITIES = 15;
const MAX_EAV = 50;
const MIN_ENTITIES_FALLBACK = 5;

/**
 * Build complete input context for a node before Content Engine runs.
 * Fetches: map, seed, node, demand, knowledge (filtered), writing profile.
 * One function, one set of DB queries — no redundant fetches in pipeline stages.
 */
export async function buildNodeContentContext(
  nodeId: string,
  mapId: string,
): Promise<NodeContentContext> {

  // 1. Fetch node
  const { data: node, error: nodeErr } = await (supabase as any)
    .from("thematic_nodes")
    .select("*")
    .eq("id", nodeId)
    .single();
  if (nodeErr || !node) throw new Error(`Node nie znaleziony: ${nodeErr?.message}`);

  // 2. Fetch seed
  const { data: seed } = await (supabase as any)
    .from("thematic_seeds")
    .select("id, name, slug, intent_family, priority")
    .eq("id", node.seed_id)
    .single();

  // 3. Fetch map
  const { data: map, error: mapErr } = await (supabase as any)
    .from("thematic_maps")
    .select("id, name, macro_statement, scope_in, scope_out, boundaries_owned, boundaries_not_owned, boundaries_adjacent, language, active_profile_id")
    .eq("id", mapId)
    .single();
  if (mapErr || !map) throw new Error(`Mapa nie znaleziona: ${mapErr?.message}`);

  // 4. Fetch writing profile
  let writingProfile: NodeContentContext["writingProfile"] = null;
  if (map.active_profile_id) {
    const { data: profile } = await (supabase as any)
      .from("thematic_writing_profiles")
      .select("*")
      .eq("id", map.active_profile_id)
      .single();
    if (profile) {
      writingProfile = {
        author_identity: profile.author_identity || "Ekspert branżowy",
        voice_pronouns: profile.voice_pronouns || "ty",
        verbosity: profile.verbosity || "standard",
        sentence_length: profile.sentence_length || "mixed",
        preferred_phrases: profile.preferred_phrases || [],
        banned_phrases: profile.banned_phrases || [],
        claim_strength: profile.claim_strength || "moderate",
        evidence_style: profile.evidence_style || "standard",
        analogy_frequency: profile.analogy_frequency || "moderate",
        analogy_domain: profile.analogy_domain || "",
        glossary_mode: profile.glossary_mode || "first_mention",
        language_rules: profile.language_rules || "",
      };
    }
  }

  // 5. Fetch relevant entities (NOT all map entities)
  const primaryEntity = (node.primary_entity_raw || "").toLowerCase();
  const requiredEntities = (node.required_entities_raw || []).map((e: string) => e.toLowerCase());

  // 5a. Get node relations to find connected entities
  const { data: nodeRelations } = await (supabase as any)
    .from("thematic_node_relations")
    .select("from_node_id, to_node_id, relation_type, weight")
    .or(`from_node_id.eq.${nodeId},to_node_id.eq.${nodeId}`)
    .eq("map_id", mapId);

  // 5b. Get connected node IDs → their entities
  const connectedNodeIds = new Set<string>();
  for (const rel of nodeRelations || []) {
    if (rel.from_node_id !== nodeId) connectedNodeIds.add(rel.from_node_id);
    if (rel.to_node_id !== nodeId) connectedNodeIds.add(rel.to_node_id);
  }

  // 5c. Fetch ALL map entities (we'll filter client-side)
  const { data: allEntities } = await (supabase as any)
    .from("thematic_entities")
    .select("id, canonical_name, entity_type, aliases, description")
    .eq("map_id", mapId);

  // 5c2. Fetch KG entity names for this node (from Etap 1 — already saved)
  const kgEntityNames = new Set<string>();
  const { data: kgRows } = await (supabase as any)
    .from("content_knowledge_graphs")
    .select("kg_data")
    .eq("node_id", nodeId)
    .limit(1);
  const kgRow = kgRows?.[0];
  if (kgRow?.kg_data?.entities) {
    for (const e of kgRow.kg_data.entities) {
      if (e.entity_name) kgEntityNames.add(e.entity_name.toLowerCase());
    }
  }

  // 5d. Filter: primary entity + required entities — with match tier tracking
  type MatchTier = "exact" | "alias" | "kg_entity" | "contains";
  const relevantEntityIds = new Set<string>();
  const entityMatchTier = new Map<string, MatchTier>(); // entity id → tier
  const relevantEntities: NodeContentContext["knowledge"]["entities"] = [];

  for (const entity of allEntities || []) {
    const name = (entity.canonical_name || "").toLowerCase();
    const aliases = (entity.aliases || []).map((a: string) => a.toLowerCase());

    // Tier 1: EXACT match on canonical name
    if (name === primaryEntity || requiredEntities.includes(name)) {
      relevantEntityIds.add(entity.id);
      entityMatchTier.set(entity.id, "exact");
      continue;
    }
    // Tier 2: ALIAS exact match
    if (aliases.some((a) => a === primaryEntity || requiredEntities.includes(a))) {
      relevantEntityIds.add(entity.id);
      entityMatchTier.set(entity.id, "alias");
      continue;
    }
    // Tier 2.5: KG entity match — encja jest w Knowledge Graph tego node'a (z SERP research)
    if (kgEntityNames.has(name) || aliases.some((a) => kgEntityNames.has(a))) {
      relevantEntityIds.add(entity.id);
      entityMatchTier.set(entity.id, "kg_entity");
      continue;
    }
    // Tier 3: CONTAINS match (substring) — entity stays in list but EAV will be excluded
    const allNames = [name, ...aliases];
    if (allNames.some((n) => (primaryEntity.length >= 3 && n.includes(primaryEntity)) || (n.length >= 3 && primaryEntity.includes(n)))) {
      relevantEntityIds.add(entity.id);
      entityMatchTier.set(entity.id, "contains");
      continue;
    }
    if (requiredEntities.some((req: string) => allNames.some((n) => (req.length >= 3 && n.includes(req)) || (n.length >= 3 && req.includes(n))))) {
      relevantEntityIds.add(entity.id);
      entityMatchTier.set(entity.id, "contains");
      continue;
    }
  }

  // 5e. Fallback: if too few entities, add top from same seed (tier=contains)
  if (relevantEntityIds.size < MIN_ENTITIES_FALLBACK && allEntities) {
    const { data: seedNodes } = await (supabase as any)
      .from("thematic_nodes")
      .select("primary_entity_raw, required_entities_raw")
      .eq("seed_id", node.seed_id)
      .eq("map_id", mapId);

    const seedEntityNames = new Set<string>();
    for (const sn of seedNodes || []) {
      if (sn.primary_entity_raw) seedEntityNames.add(sn.primary_entity_raw.toLowerCase());
      for (const re of sn.required_entities_raw || []) seedEntityNames.add(re.toLowerCase());
    }

    for (const entity of allEntities) {
      if (relevantEntityIds.size >= MAX_ENTITIES) break;
      if (relevantEntityIds.has(entity.id)) continue;
      const name = (entity.canonical_name || "").toLowerCase();
      if (seedEntityNames.has(name)) {
        relevantEntityIds.add(entity.id);
        entityMatchTier.set(entity.id, "contains");
      }
    }
  }

  // 5f. Build final entities list (capped at MAX_ENTITIES)
  for (const entity of allEntities || []) {
    if (relevantEntities.length >= MAX_ENTITIES) break;
    if (relevantEntityIds.has(entity.id)) {
      relevantEntities.push({
        canonical_name: entity.canonical_name,
        entity_type: entity.entity_type,
        aliases: entity.aliases || [],
        description: entity.description || "",
      });
    }
  }

  // 6. Fetch EAV — for exact/alias/kg_entity tier entities (not contains)
  const eavEntityIds = [...relevantEntityIds].filter((id) => {
    const tier = entityMatchTier.get(id);
    return tier === "exact" || tier === "alias" || tier === "kg_entity";
  });
  const eavExcludedIds = [...relevantEntityIds].filter((id) => entityMatchTier.get(id) === "contains");

  // Debug: log entity tiers
  const entityIdToName = new Map<string, string>();
  for (const e of allEntities || []) entityIdToName.set(e.id, e.canonical_name);

  logDebug("CTX", "Entity match tiers", Object.fromEntries([...entityMatchTier.entries()].map(([id, tier]) => [entityIdToName.get(id) || id, tier])));
  const kgMatchedIds = [...relevantEntityIds].filter((id) => entityMatchTier.get(id) === "kg_entity");
  if (kgMatchedIds.length > 0) {
    logDebug("CTX", `EAV kg_entity match: ${kgMatchedIds.map((id) => entityIdToName.get(id) || id).join(", ")} (KG entities z SERP research tego node'a)`);
  }
  if (eavExcludedIds.length > 0) {
    logDebug("CTX", `EAV excluded (contains-only): ${eavExcludedIds.map((id) => entityIdToName.get(id) || id).join(", ")}`);
  }

  let eav: NodeContentContext["knowledge"]["eav"] = [];

  // Fetch EAV in 2 tiers: exact/alias (full) + kg_entity (limited to avoid noise)
  const exactAliasIds = eavEntityIds.filter((id) => {
    const tier = entityMatchTier.get(id);
    return tier === "exact" || tier === "alias";
  });
  const kgOnlyIds = eavEntityIds.filter((id) => entityMatchTier.get(id) === "kg_entity");

  if (exactAliasIds.length > 0) {
    const { data: eavData } = await (supabase as any)
      .from("thematic_entity_attributes")
      .select("entity_id, attribute_name, value, confidence")
      .in("entity_id", exactAliasIds)
      .gte("confidence", 0.5)
      .order("confidence", { ascending: false })
      .limit(MAX_EAV);
    for (const a of eavData || []) {
      eav.push({ entity_name: entityIdToName.get(a.entity_id) || a.entity_id, attribute_name: a.attribute_name, value: a.value, confidence: a.confidence });
    }
  }

  // kg_entity EAV — max 3 per entity, max 15 total (avoid flooding with irrelevant map-wide EAV)
  if (kgOnlyIds.length > 0) {
    const { data: kgEavData } = await (supabase as any)
      .from("thematic_entity_attributes")
      .select("entity_id, attribute_name, value, confidence")
      .in("entity_id", kgOnlyIds)
      .gte("confidence", 0.7)
      .order("confidence", { ascending: false })
      .limit(30);
    // Group by entity, take max 3 per entity
    const perEntityCount = new Map<string, number>();
    let kgEavTotal = 0;
    for (const a of kgEavData || []) {
      if (kgEavTotal >= 15) break;
      const count = perEntityCount.get(a.entity_id) || 0;
      if (count >= 3) continue;
      perEntityCount.set(a.entity_id, count + 1);
      kgEavTotal++;
      eav.push({ entity_name: entityIdToName.get(a.entity_id) || a.entity_id, attribute_name: a.attribute_name, value: a.value, confidence: a.confidence });
    }
    if (kgEavTotal > 0) {
      logDebug("CTX", `EAV kg_entity: ${kgEavTotal} items from ${perEntityCount.size} entities (max 3/entity, 15 total)`);
    }
  }

  // 7. Build relations + internal link suggestions
  const { data: allNodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("id, title, slug")
    .eq("map_id", mapId);

  const nodeIdToInfo = new Map<string, { title: string; slug: string }>();
  for (const n of allNodes || []) nodeIdToInfo.set(n.id, { title: n.title, slug: n.slug });

  const relations: NodeContentContext["knowledge"]["relations"] = [];
  const internalLinks: NodeContentContext["knowledge"]["internal_link_suggestions"] = [];

  for (const rel of nodeRelations || []) {
    const fromInfo = nodeIdToInfo.get(rel.from_node_id);
    const toInfo = nodeIdToInfo.get(rel.to_node_id);
    if (!fromInfo || !toInfo) continue;

    relations.push({
      from_title: fromInfo.title,
      from_slug: fromInfo.slug,
      to_title: toInfo.title,
      to_slug: toInfo.slug,
      relation_type: rel.relation_type,
      weight: rel.weight,
    });

    // Internal link suggestions: nodes this node should link TO
    const targetId = rel.from_node_id === nodeId ? rel.to_node_id : rel.from_node_id;
    const targetInfo = nodeIdToInfo.get(targetId);
    if (targetInfo && !internalLinks.some((l) => l.target_slug === targetInfo.slug)) {
      internalLinks.push({
        target_slug: targetInfo.slug,
        target_title: targetInfo.title,
        relation_type: rel.relation_type,
      });
    }
  }

  // 7b. Fallback: if few internal links, add top nodes from same seed
  if (internalLinks.length < 3) {
    const { data: seedNodes } = await (supabase as any)
      .from("thematic_nodes")
      .select("id, title, slug, publish_priority")
      .eq("seed_id", node.seed_id)
      .eq("map_id", mapId)
      .neq("id", nodeId)
      .order("publish_priority", { ascending: false })
      .limit(5);

    for (const sn of seedNodes || []) {
      if (internalLinks.length >= 5) break;
      if (!internalLinks.some((l) => l.target_slug === sn.slug)) {
        internalLinks.push({
          target_slug: sn.slug,
          target_title: sn.title,
          relation_type: "seed_sibling",
        });
      }
    }
  }

  // 7c. Existing Posts Awareness: pool WP posts z domeny mapy → internal_link_suggestions
  // Format identyczny dla LLM (markdown link list). relation_type='wp_existing' sygnalizuje
  // draftGenerator aby uzyl absolutnego URL zamiast /slug.
  try {
    const wpPool = await getWpPostsForLinking(mapId, nodeId, 10);
    for (const wp of wpPool) {
      if (!wp.url) continue;
      if (internalLinks.some((l) => l.target_slug === wp.url)) continue;
      internalLinks.push({
        target_slug: wp.url, // full URL — draftGenerator wykryje wp_existing i uzyje go bez prefix "/"
        target_title: wp.title,
        relation_type: "wp_existing",
      });
    }
    if (wpPool.length > 0) {
      logDebug("CTX", `+${wpPool.length} WP posts do internal_link_suggestions pool`);
    }
  } catch (err) {
    // Matching nie moze zepsuc pipeline — loguj i kontynuuj
    logError("CTX", `WP link pool load failed: ${err instanceof Error ? err.message : err}`);
  }

  // 8. Extract Reverse SERP data from demand_source JSONB
  const ds = (node.demand_source || {}) as Record<string, unknown>;
  const serpData: NodeContentContext["serp"] = ds.source === "reverse_serp" ? {
    validated_question: (ds.validated_question as string) || null,
    best_market_keyword: (ds.best_market_keyword as string) || null,
    market_keywords_top5: ((ds.market_keywords as any[]) || []).slice(0, 5),
    serp_decision: (ds.decision as string) || null,
    confidence: (ds.confidence as string) || null,
    serp_mode: (ds.serp_mode as string) || null,
    top3_competitors: ((ds.top3_competitors as any[]) || []).map((c: any) => ({
      domain: c.domain || "", title: c.title || "",
    })).slice(0, 3),
    candidate_sv: (ds.candidate_sv as number) || 0,
    market_keyword_sv: (ds.market_keyword_sv as number) || 0,
  } : null;

  // 8b. Existing Posts Awareness: refurbish_context (meta info o starym wpisie)
  // CELOWO bez starego HTML — LLM widzac stary tekst robi kosmetyczne poprawki zamiast pisac od zera.
  // Trzymamy w ctx.refurbish_context TYLKO metadata (url, title, word_count, red_flags).
  let refurbishContext: NodeContentContext["refurbish_context"] = null;
  if (node.match_status === "matched_refurbish" && node.matched_wp_post_id) {
    const { data: wpForRef } = await (supabase as any)
      .from("wp_posts")
      .select("title, wp_url, word_count, quality_red_flags")
      .eq("id", node.matched_wp_post_id)
      .maybeSingle();
    if (wpForRef) {
      refurbishContext = {
        is_refurbish: true,
        existing_wp_post_url: wpForRef.wp_url || "",
        existing_wp_title: wpForRef.title || "",
        existing_wp_word_count: wpForRef.word_count ?? null,
        existing_wp_red_flags: (wpForRef.quality_red_flags as string[]) || [],
      };
      logDebug("CTX", `Refurbish context: url=${wpForRef.wp_url} flags=${refurbishContext.existing_wp_red_flags.length}`);
    }
  }

  // 9. Build final context
  const ctx: NodeContentContext = {
    map: {
      id: map.id,
      name: map.name,
      macro_statement: map.macro_statement || "",
      scope_in: map.scope_in || [],
      scope_out: map.scope_out || [],
      boundaries_owned: map.boundaries_owned || [],
      boundaries_not_owned: map.boundaries_not_owned || [],
      boundaries_adjacent: map.boundaries_adjacent || [],
      language: map.language || "pl",
    },
    seed: {
      id: seed?.id || node.seed_id,
      name: seed?.name || "",
      slug: seed?.slug || "",
      intent_family: seed?.intent_family || [],
      priority: seed?.priority || 3,
    },
    node: {
      id: node.id,
      title: node.title,
      slug: node.slug,
      document_role: node.document_role,
      primary_intent: node.primary_intent || "",
      canonical_question: node.canonical_question || node.title,
      primary_entity: node.primary_entity_raw || "",
      required_entities: node.required_entities_raw || [],
    },
    demand: {
      search_volume: node.search_volume || null,
      cpc: node.cpc || null,
      keyword_difficulty: node.keyword_difficulty || null,
      commercial_score: node.commercial_score || null,
      demand_tier: node.demand_tier || "unknown",
      node_role: node.node_role || "spoke",
      publish_priority: node.publish_priority || 50,
    },
    knowledge: {
      entities: relevantEntities,
      eav,
      relations,
      internal_link_suggestions: internalLinks,
    },
    writingProfile,
    serp: serpData,
    refurbish_context: refurbishContext,
  };

  logPublic("CTX", `"${node.title}" — ${relevantEntities.length} encji, ${eav.length} EAV, ${relations.length} relacji`);
  logDebug("CTX", `NodeContentContext details`, {
    entities: relevantEntities.length,
    eav: eav.length,
    relations: relations.length,
    internalLinks: internalLinks.length,
    hasProfile: !!writingProfile,
    demandTier: node.demand_tier,
    serpData: serpData ? `decision=${serpData.serp_decision} market=${serpData.best_market_keyword} top5=${serpData.market_keywords_top5.length}` : "none",
  } as any);

  return ctx;
}

/**
 * Create a lightweight snapshot for engine_debug.input_context.
 */
export function createContextSnapshot(ctx: NodeContentContext): Record<string, unknown> {
  return {
    map_id: ctx.map.id,
    map_name: ctx.map.name,
    seed_id: ctx.seed.id,
    seed_name: ctx.seed.name,
    node_id: ctx.node.id,
    node_title: ctx.node.title,
    document_role: ctx.node.document_role,
    primary_entity: ctx.node.primary_entity,
    required_entities: ctx.node.required_entities,
    demand_tier: ctx.demand.demand_tier,
    node_role: ctx.demand.node_role,
    entities_count: ctx.knowledge.entities.length,
    eav_count: ctx.knowledge.eav.length,
    relations_count: ctx.knowledge.relations.length,
    internal_links_count: ctx.knowledge.internal_link_suggestions.length,
    has_writing_profile: !!ctx.writingProfile,
    top_entities: ctx.knowledge.entities.slice(0, 5).map((e) => e.canonical_name),
    internal_link_suggestions: ctx.knowledge.internal_link_suggestions.map((l) => l.target_title),
  };
}

// =============================================
// Context Hash — deterministic hash for stale detection
// =============================================

function sortedStrings(arr: string[]): string[] {
  return [...arr].map((s) => s.trim()).sort();
}

/**
 * Build a deterministic hash from NodeContentContext.
 * Only fields that affect content generation are included.
 * Arrays sorted, strings trimmed, numbers rounded.
 */
export function buildContextHash(ctx: NodeContentContext): string {
  const payload = {
    map: {
      macro: (ctx.map.macro_statement || "").trim(),
      scope_in: sortedStrings(ctx.map.scope_in),
      scope_out: sortedStrings(ctx.map.scope_out),
      boundaries_owned: sortedStrings(ctx.map.boundaries_owned),
      boundaries_not_owned: sortedStrings(ctx.map.boundaries_not_owned),
      boundaries_adjacent: sortedStrings(ctx.map.boundaries_adjacent),
      language: ctx.map.language,
    },
    seed: {
      name: ctx.seed.name.trim(),
      intent_family: sortedStrings(ctx.seed.intent_family),
      priority: ctx.seed.priority,
    },
    node: {
      title: ctx.node.title.trim(),
      canonical_question: ctx.node.canonical_question.trim(),
      document_role: ctx.node.document_role,
      primary_entity: ctx.node.primary_entity.trim(),
      required_entities: sortedStrings(ctx.node.required_entities),
    },
    demand: {
      demand_tier: ctx.demand.demand_tier,
      publish_priority: ctx.demand.publish_priority,
      commercial_score: ctx.demand.commercial_score != null
        ? Math.round(ctx.demand.commercial_score * 100) / 100
        : null,
    },
    knowledge: {
      entities: ctx.knowledge.entities.map((e) => e.canonical_name.trim()).sort(),
      eav: ctx.knowledge.eav
        .map((a) => `${a.entity_name.trim()}::${a.attribute_name.trim()}::${a.value.trim()}`)
        .sort(),
      relations: ctx.knowledge.relations
        .map((r) => `${r.from_slug}→${r.to_slug}::${r.relation_type}`)
        .sort(),
      links: ctx.knowledge.internal_link_suggestions
        .map((l) => l.target_slug)
        .sort(),
    },
    profile: ctx.writingProfile ? {
      voice: ctx.writingProfile.voice_pronouns,
      banned: sortedStrings(ctx.writingProfile.banned_phrases),
      preferred: sortedStrings(ctx.writingProfile.preferred_phrases),
    } : null,
  };

  const json = JSON.stringify(payload);

  // djb2 hash (same as deduplicationService)
  let hash1 = 5381;
  let hash2 = 52711;
  for (let i = 0; i < json.length; i++) {
    const ch = json.charCodeAt(i);
    hash1 = ((hash1 << 5) + hash1 + ch) | 0;
    hash2 = ((hash2 << 5) + hash2 + ch) | 0;
  }
  return ((hash1 >>> 0).toString(16).padStart(8, "0")) + ((hash2 >>> 0).toString(16).padStart(8, "0"));
}

// =============================================
// Content Freshness Check
// =============================================

/**
 * Check if content for a node is still fresh (context hasn't changed).
 * Safe for legacy records without hash — does NOT mark them as stale.
 */
export async function checkContentFreshness(
  nodeId: string,
  mapId: string,
): Promise<{ isStale: boolean; isLegacy: boolean; currentHash: string }> {
  const ctx = await buildNodeContentContext(nodeId, mapId);
  const currentHash = buildContextHash(ctx);

  const { data } = await (supabase as any)
    .from("thematic_node_content")
    .select("generated_from_context_hash")
    .eq("node_id", nodeId)
    .maybeSingle();

  const generatedHash = data?.generated_from_context_hash ?? null;

  // Legacy record — no hash yet. Save current but DON'T mark as stale.
  if (generatedHash === null) {
    await (supabase as any)
      .from("thematic_node_content")
      .update({
        current_context_hash: currentHash,
        last_context_checked_at: new Date().toISOString(),
      })
      .eq("node_id", nodeId);
    return { isStale: false, isLegacy: true, currentHash };
  }

  const isStale = generatedHash !== currentHash;

  await (supabase as any)
    .from("thematic_node_content")
    .update({
      current_context_hash: currentHash,
      is_stale: isStale,
      needs_regeneration: isStale,
      last_context_checked_at: new Date().toISOString(),
    })
    .eq("node_id", nodeId);

  return { isStale, isLegacy: false, currentHash };
}

/**
 * Refresh freshness for all nodes with content in a map.
 */
export async function refreshMapContentFreshness(
  mapId: string,
): Promise<{ checked: number; stale: number }> {
  const { data: contentNodes } = await (supabase as any)
    .from("thematic_node_content")
    .select("node_id")
    .eq("map_id", mapId);

  let checked = 0;
  let stale = 0;

  for (const cn of contentNodes || []) {
    try {
      const result = await checkContentFreshness(cn.node_id, mapId);
      checked++;
      if (result.isStale) stale++;
    } catch {
      // Skip failed nodes
    }
  }

  return { checked, stale };
}
